/**
 * useRestaurantMedia Hook
 *
 * Purpose: Manage restaurant media operations with React Query integration
 *
 * Features:
 * - Fetch images/videos with automatic caching
 * - Upload images with progress tracking
 * - Delete media with optimistic updates
 * - Generate AI images/videos (future)
 * - Toast notifications for all operations
 *
 * Used in: Media Library Dashboard, MediaPicker (for email editor)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import MediaService, {
  RestaurantMedia,
  UploadMediaParams,
  GenerateImageParams,
  GenerateVideoParams
} from '@/api/media';

interface UseRestaurantMediaFilters {
  restaurant_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * Hook for managing restaurant images
 */
export const useRestaurantImages = (
  organisationId: string,
  filters?: UseRestaurantMediaFilters
) => {
  const queryClient = useQueryClient();

  // Fetch images
  const {
    data: images = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['restaurant-images', organisationId, filters],
    queryFn: () => MediaService.fetchImages(organisationId, filters),
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Upload image mutation
  const uploadMutation = useMutation({
    mutationFn: (params: UploadMediaParams) => MediaService.uploadImage(params),
    onSuccess: (newImage) => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-images'] });
      toast({
        title: "Upload successful",
        description: newImage.original_filename
          ? `${newImage.original_filename} has been uploaded`
          : "Image uploaded successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Upload failed",
        description: error.message || 'Failed to upload image',
        variant: "destructive"
      });
    }
  });

  // Generate AI image mutation
  const generateMutation = useMutation({
    mutationFn: (params: GenerateImageParams) => MediaService.generateImage(params),
    onSuccess: (newImage) => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-images'] });
      toast({
        title: "Image generated",
        description: "AI image has been generated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Generation failed",
        description: error.message || 'Failed to generate image',
        variant: "destructive"
      });
    }
  });

  // Delete image mutation
  const deleteMutation = useMutation({
    mutationFn: (imageId: string) => MediaService.deleteImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-images'] });
      toast({
        title: "Image deleted",
        description: "The image has been removed from your library"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error.message || 'Failed to delete image',
        variant: "destructive"
      });
    }
  });

  return {
    images,
    isLoading,
    error,
    refetch,
    uploadImage: uploadMutation.mutate,
    uploadImageAsync: uploadMutation.mutateAsync,
    generateImage: generateMutation.mutate,
    generateImageAsync: generateMutation.mutateAsync,
    deleteImage: deleteMutation.mutate,
    isUploading: uploadMutation.isPending,
    isGenerating: generateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
};

/**
 * Hook for managing restaurant videos
 */
export const useRestaurantVideos = (
  organisationId: string,
  filters?: UseRestaurantMediaFilters
) => {
  const queryClient = useQueryClient();

  // Fetch videos
  const {
    data: videos = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['restaurant-videos', organisationId, filters],
    queryFn: () => MediaService.fetchVideos(organisationId, filters),
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Generate AI video mutation
  const generateMutation = useMutation({
    mutationFn: (params: GenerateVideoParams) => MediaService.generateVideo(params),
    onSuccess: (newVideo) => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-videos'] });
      toast({
        title: "Video generation started",
        description: "Your video is being generated. This may take a few minutes.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Generation failed",
        description: error.message || 'Failed to generate video',
        variant: "destructive"
      });
    }
  });

  // Delete video mutation
  const deleteMutation = useMutation({
    mutationFn: (videoId: string) => MediaService.deleteVideo(videoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-videos'] });
      toast({
        title: "Video deleted",
        description: "The video has been removed from your library"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error.message || 'Failed to delete video',
        variant: "destructive"
      });
    }
  });

  return {
    videos,
    isLoading,
    error,
    refetch,
    generateVideo: generateMutation.mutate,
    generateVideoAsync: generateMutation.mutateAsync,
    deleteVideo: deleteMutation.mutate,
    isGenerating: generateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
};

/**
 * Hook for fetching a single image (for details view)
 */
export const useImage = (imageId: string | null) => {
  return useQuery({
    queryKey: ['image', imageId],
    queryFn: () => MediaService.getImage(imageId!),
    enabled: !!imageId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching a single video (for details view)
 */
export const useVideo = (videoId: string | null) => {
  return useQuery({
    queryKey: ['video', videoId],
    queryFn: () => MediaService.getVideo(videoId!),
    enabled: !!videoId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching video job status (for polling during generation)
 *
 * Polls every 10 seconds while status is queued or processing.
 * Continues polling for one more cycle when completed to ensure
 * we get the final state with public_url.
 */
export const useVideoJobStatus = (jobId: string | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['video-job-status', jobId],
    queryFn: async () => {
      console.log('[useVideoJobStatus] Fetching status for job:', jobId);
      const result = await MediaService.fetchJobStatus(jobId!);
      const hasUrl = !!(result.public_url || result.video_url);
      console.log('[useVideoJobStatus] Got status:', result.status, 'has_url:', hasUrl, 'public_url:', !!result.public_url, 'video_url:', !!result.video_url);
      return result;
    },
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      // Get the data from the query result
      const data = query.state.data;

      // Auto-refetch every 10 seconds if job is still active
      if (data && (data.status === 'queued' || data.status === 'processing')) {
        console.log('[useVideoJobStatus] Continuing poll - status:', data.status, 'progress:', data.progress);
        return 10000; // 10 seconds
      }

      // CRITICAL FIX: If completed but no URL yet, poll once more after 2 seconds
      // This handles race condition where backend sets status to 'completed'
      // but hasn't finished uploading video and setting public_url/video_url yet
      const hasUrl = data && (data.public_url || data.video_url);
      if (data && data.status === 'completed' && !hasUrl) {
        console.log('[useVideoJobStatus] Completed but no URL yet - polling once more in 2s');
        return 2000; // 2 seconds
      }

      // Stop polling when completed with URL or failed
      console.log('[useVideoJobStatus] Stopping poll - final status:', data?.status, 'has_url:', !!hasUrl);
      return false;
    },
    staleTime: 0, // Always fetch fresh status
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};
